import { useMemo, useState } from "react";

function num(value) {
  return Number(value || 0);
}

function round1(value) {
  return Math.round(num(value) * 10) / 10;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDate(date) {
  return new Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "short",
  }).format(date);
}

function getTaskLabel(row = {}) {
  return row.label || row.name || row.activity || row.meta?.labourType || "Task";
}

function getTaskHours(row = {}) {
  return num(row.hours ?? row.quantity ?? row.qty ?? row.estimatedHours);
}

function getTaskGroup(row = {}) {
  return (
    row.section ||
    row.sourceLabel ||
    row.meta?.sourceLabel ||
    row.meta?.sourceType ||
    "General"
  );
}

function buildTasks(
  estimate = {},
  workdayHours = 7.5,
  startDate = new Date(),
  resourceCount = 1
) {
  const safeWorkdayHours = Math.max(1, Number(workdayHours || 7.5));
  const safeResources = Math.max(1, Number(resourceCount || 1));
  const dailyCapacityHours = safeWorkdayHours * safeResources;

  const source =
    Array.isArray(estimate.timeRows) && estimate.timeRows.length
      ? estimate.timeRows
      : estimate.labour || [];

  let cursorHours = 0;

  return source
    .filter((row) => getTaskHours(row) > 0)
    .map((row, index) => {
      const hours = getTaskHours(row);

      const durationDays = Math.max(0.25, hours / dailyCapacityHours);
      const startOffsetDays = cursorHours / dailyCapacityHours;
      const endOffsetDays = startOffsetDays + durationDays;

      const task = {
        id: row.id || `task_${index}`,
        label: getTaskLabel(row),
        group: getTaskGroup(row),
        hours: round1(hours),
        resourceCount: safeResources,
        dailyCapacityHours,
        durationDays: round1(durationDays),
        startOffsetDays: round1(startOffsetDays),
        endOffsetDays: round1(endOffsetDays),
        startDate: addDays(startDate, startOffsetDays),
        endDate: addDays(startDate, endOffsetDays),
        progress: num(row.progress ?? row.meta?.progress ?? 0),
        source: row.source || row.type || "Labour",
      };

      cursorHours += hours;

      return task;
    });
}

export default function TimeGantt({ estimate }) {
  const [workdayHours, setWorkdayHours] = useState(7.5);
  const [startDateValue, setStartDateValue] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [groupBySection, setGroupBySection] = useState(true);

  const startDate = useMemo(
    () => new Date(`${startDateValue}T08:00:00`),
    [startDateValue]
  );

  const [resourceCount, setResourceCount] = useState(1);

  const tasks = useMemo(
  () => buildTasks(estimate, workdayHours, startDate, resourceCount),
  [estimate, workdayHours, startDate, resourceCount]
);

  const totalHours = round1(tasks.reduce((sum, task) => sum + task.hours, 0));
  const totalDays = round1(
  totalHours / (workdayHours * Math.max(1, resourceCount))
);
  const maxEnd = Math.max(...tasks.map((task) => task.endOffsetDays), 1);

  const groupedTasks = useMemo(() => {
    if (!groupBySection) return [{ group: "All tasks", tasks }];

    const map = new Map();

    tasks.forEach((task) => {
      if (!map.has(task.group)) {
        map.set(task.group, []);
      }

      map.get(task.group).push(task);
    });

    return Array.from(map.entries()).map(([group, groupTasks]) => ({
      group,
      tasks: groupTasks,
    }));
  }, [tasks, groupBySection]);

  if (!tasks.length) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-950">Gantt plan</h3>
        <p className="mt-2 text-sm text-slate-500">
          No time rows available yet.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-950">Gantt plan</h3>
            <p className="text-xs text-slate-500">
              Sequential schedule based on labour hours and workday length.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-medium text-slate-500">
              Start
              <input
                type="date"
                value={startDateValue}
                onChange={(e) => setStartDateValue(e.target.value)}
                className="ml-2 h-8 rounded-md border border-slate-300 px-2 text-sm text-slate-900"
              />
            </label>

            <label className="text-xs font-medium text-slate-500">
  Hours/day
  <input
    type="number"
    value={workdayHours}
    min="1"
    max="24"
    step="0.5"
    onChange={(e) => setWorkdayHours(Number(e.target.value))}
    className="ml-2 h-8 w-20 rounded-md border border-slate-300 px-2 text-sm text-slate-900"
  />
</label>

<label className="text-xs font-medium text-slate-500">
  Labourers
  <input
    type="number"
    value={resourceCount}
    min="1"
    max="20"
    step="1"
    onChange={(e) =>
      setResourceCount(Math.max(1, Number(e.target.value)))
    }
    className="ml-2 h-8 w-20 rounded-md border border-slate-300 px-2 text-sm text-slate-900"
  />
</label>

<button
              type="button"
              onClick={() => setGroupBySection((value) => !value)}
              className="h-8 rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {groupBySection ? "Grouped" : "Flat"}
            </button>
          </div>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <MiniStat label="Total hours" value={`${totalHours} h`} />
          <MiniStat label="Estimated duration" value={`${totalDays} days`} />
          <MiniStat
            label="Finish"
            value={formatDate(addDays(startDate, totalDays))}
          />
        </div>
      </div>

      <div className="overflow-auto p-4">
        <div className="min-w-[980px] space-y-5">
          <TimelineHeader maxEnd={maxEnd} startDate={startDate} />

          {groupedTasks.map((group) => (
            <div key={group.group} className="space-y-2">
              <div className="sticky left-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {group.group}
              </div>

              {group.tasks.map((task) => {
                const left = (task.startOffsetDays / maxEnd) * 100;
                const width = Math.max(
                  ((task.endOffsetDays - task.startOffsetDays) / maxEnd) * 100,
                  2
                );

                return (
                  <div
                    key={task.id}
                    className="grid grid-cols-[260px_1fr_90px_110px] items-center gap-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {task.label}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatDate(task.startDate)} → {formatDate(task.endDate)}
                      </div>
                    </div>

                    <div className="relative h-9 rounded-lg bg-slate-100">
                      <div
                        className="absolute top-1 h-7 rounded-md bg-blue-600"
                        style={{ left: `${left}%`, width: `${width}%` }}
                      >
                        {task.progress > 0 && (
                          <div
                            className="h-full rounded-md bg-blue-800/40"
                            style={{
                              width: `${Math.min(task.progress, 100)}%`,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="text-right text-sm font-semibold text-slate-900">
                      {task.hours} h
                    </div>

                    <div className="text-right text-xs text-slate-500">
                      {task.durationDays} days
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function TimelineHeader({ maxEnd, startDate }) {
  const ticks = Array.from({ length: Math.ceil(maxEnd) + 1 }, (_, index) => index);

  return (
    <div className="grid grid-cols-[260px_1fr_90px_110px] gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      <div>Task</div>

      <div className="relative h-8 rounded-lg bg-slate-50">
        {ticks.map((tick) => (
          <div
            key={tick}
            className="absolute top-0 h-8 border-l border-slate-200 pl-1"
            style={{ left: `${(tick / maxEnd) * 100}%` }}
          >
            {formatDate(addDays(startDate, tick))}
          </div>
        ))}
      </div>

      <div className="text-right">Hours</div>
      <div className="text-right">Duration</div>
    </div>
  );
}